/**
 * @format
 */
import AppPopup from '@/components/popups/app-popup';
import SolutionPreviewPopup from '@/components/popups/solution-preview-popup';
import EventBus from '@/scripts/event-bus';
import { SolutionService } from '@/scripts/services/solution/solution-service';
import { LogLevel } from '@/scripts/logger';

export const SolutionBoardServiceMixin = {
    data() {
        return {
            selectedItems: [],
            deletingItems: [],
        };
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },

        agencyId() {
            return this.$store.state.context.agency.id;
        },

        clientId() {
            return this.$store.state.context.client.id;
        },

        baseUrl() {
            return this.$store.getters.baseUrl;
        },
    },

    methods: {
        refreshEvent() {
            EventBus.$emit('solution:update');
        },

        addSolution() {
            EventBus.$emit('open-widget', {
                name: 'solution-widget',
                accountId: this.accountId,
                action: 'Add New',
            });
        },

        editSolution(item) {
            EventBus.$emit('open-widget', {
                name: 'solution-widget',
                solutionId: item.id,
                accountId: this.accountId,
                action: 'Edit',
            });
        },

        addToMySolutions({ id }) {
            SolutionService.addToMySolutions(this.accountId, id)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'The Vendor Solution has been added to your solutions.',
                    );
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while adding the Vendor Solution to your solutions.',
                        { error },
                    );
                });
        },

        duplicateSolution({ id, name }) {
            const widget = {
                name: 'duplicate-widget',
                entityId: id,
                entityName: name,
                serviceName: 'Solutions',
                entityType: 'Solution',
                fieldLabel: 'Choose a name for the new solution',
                fieldPlaceHolder: 'New Solution Name',
                replaceEntityField: 'Name',
                onSuccess: () => {
                    this.refreshEvent();
                },
            };

            EventBus.$emit('open-widget', widget);
        },

        onPublish(item) {
            if (!item.isAutoPublished) {
                const widget = {
                    name: 'vendor-solution-publish-widget',
                    options: {
                        solutionId: item.id,
                    },
                };

                EventBus.$emit('open-widget', widget);
            } else {
                this.$helpers.showFailNotification('This solution is already set to Auto Publish', {
                    level: LogLevel.INFO,
                });
            }
        },

        onUnpublish({ id }) {
            SolutionService.unpublish(id)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your publish settings have been updated.',
                    );
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while updating your solution publish settings.',
                        { error },
                    );
                });
        },

        deleteItem(item) {
            this.deletingItems = [item];

            SolutionService.delete(item.id)
                .then(() => {
                    this.$helpers.showSuccessNotification('The solution has been deleted.');
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while deleting your solution.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loading = false;
                    this.deletingItems = [];
                });
        },

        // Popups
        showSolutionDetailsPopup(item) {
            this.$modal.show(
                SolutionPreviewPopup,
                {
                    accountId: this.accountId,
                    solution: item.data,
                    actions: {},
                },
                {
                    classes: 'lightbox-popup',
                    height: '100%',
                    width: '803px',
                },
            );
        },

        showDeletePopup(item) {
            this.actions = {
                confirm: {
                    label: 'Delete',
                    action: () => {
                        this.deleteItem(item);
                    },
                },

                cancel: {
                    label: 'Cancel',
                },
            };

            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Are you sure?',
                    content:
                        '<p>The selected solution will be deleted. You can not undo this action.</p>',
                    actions: this.actions,
                    confirmText: 'Delete',
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        mapCategoriesItem(items) {
            return items.map((item) => ({
                name: item.Name,
            }));
        },

        mapSolutionBundles(items) {
            return items.map((item) => ({
                id: item.Id,
                name: item.Name,
                description: item.Description,
                imageUrl: item.ImageUrl,
                price: item.Price,
                quantity: item.Quantity,
                fileMimeType: item.FileMimeType,
                isPreviewImageSvg: this.$helpers.isDocumentType(item.FileMimeType, 'svg'),
            }));
        },

        // Table mapper
        solutionItems(items) {
            return items.map((item) => {
                const billingCycles = this.$helpers.getSolutionBillingCycles();
                const billingTypes = this.$helpers.getSolutionBillingTypes();
                const solutionTypes = this.$helpers.getSolutionTypes();

                const billingCycle = billingCycles.find(({ value }) => value === item.BillingCycle);
                const billingType = billingTypes.find(({ value }) => value === item.BillingTypeId);
                const solutionType = solutionTypes.find(
                    ({ value }) => value === item.SolutionTypeId,
                );

                const solutionItem = {
                    id: item.Id,
                    accountId: item.AccountId,
                    name: item.Name,
                    sku: item.Sku,
                    solutionType: solutionType ? solutionType.name : null,
                    categories: this.mapCategoriesItem(item.Categories),
                    category: item.Categories.map((category) => category.Name).join(', '),
                    description: item.Description,
                    bundles: this.mapSolutionBundles(item.Bundles),
                    unitPrice: item.UnitPrice,
                    unitCost: item.UnitCost,
                    unitMargin: item.UnitMargin,
                    billingType: billingType ? billingType.name : null,
                    billingCycle: billingCycle ? billingCycle.name : null,
                    previewImageUrl: item.Url,
                    fileMimeType: item.FileMimeType,
                    isPreviewImageSvg: this.$helpers.isDocumentType(item.FileMimeType, 'svg'),
                    videoUrl: item.VideoUrl,
                    videoThumbnail: item.VideoThumbnailUrl,
                    createdAt: this.$moment(item.CreatedAt),
                    updatedAt: this.$moment(item.UpdatedAt),
                    publishedAt: item.PublishedAt ? this.$moment(item.PublishedAt) : null,

                    autoPublished: {
                        solutionId: item.Id,
                        name: item.Name,
                        isAutoPublished: item.IsAutoPublished,
                        isLoading: false,
                    },

                    asset: {
                        solutionId: item.Id,
                        isAutoPublished: item.IsAutoPublished,
                        published: item['SharedAssets@odata.count'],
                    },

                    isAutoPublished: item.IsAutoPublished,
                    published: item['SharedAssets@odata.count'] || null,
                };

                if (item.Account) {
                    const { Agency } = item.Account;
                    solutionItem.vendor = {
                        id: Agency.Id,
                        link: null,
                        name: Agency.Name,
                        logoUrl: Agency.LogoImageUrl,
                    };
                }

                return solutionItem;
            });
        },

        // Query methods
        getFilterParam(options, type) {
            const queryFilters = [];

            if (options.search) {
                queryFilters.push(this.getSearchFilter(options.search));
            }

            if (type === 'shared') {
                return queryFilters.length ? queryFilters : null;
            }

            if (this.accountId) {
                queryFilters.push(`AccountId eq ${this.accountId}`);
            }

            switch (type) {
                case 'published':
                    queryFilters.push('SharedAssets/any()');
                    break;
                case 'unpublished':
                    queryFilters.push('not SharedAssets/any()');
                    break;
            }

            return queryFilters.length ? queryFilters.join(' and ') : null;
        },

        getSearchFilter(search) {
            const encodedSearch = encodeURIComponent(search);
            return `(contains(Name, '${encodedSearch}'))`;
        },

        getPageParam(options) {
            return options.itemsPerPage && options.page
                ? `${options.itemsPerPage * (options.page - 1)}`
                : null;
        },

        getTopParam(options) {
            return options.itemsPerPage ? `${options.itemsPerPage}` : null;
        },

        getOrderParam(options) {
            return options.sort ? `${options.sort.property} ${options.sort.direction}` : null;
        },

        getExpandParam(type) {
            const query = [
                'Categories($select=Name)',
                'Bundles($select=Id,Name,Description,ImageUrl,Price,Quantity,FileMimeType)',
            ];

            if (type === 'shared') {
                query.push('Account($select=Id;$expand=Agency($select=Id,Name,LogoImageUrl))');
            } else if (type === 'published') {
                query.push('SharedAssets($count=true;$top=0)');
            }

            return query.join(',');
        },

        createParamsFromBoardQuery(options, type) {
            return {
                noOdata: type === 'shared' ? `clientId=${this.clientId}` : '',
                filter: this.getFilterParam(options, type),
                skip: this.getPageParam(options),
                top: this.getTopParam(options),
                orderBy: this.getOrderParam(options),
                expand: this.getExpandParam(type),
                count: true,
            };
        },

        // Get Solutions
        dispatchGetSolutions({ options = {}, type = null, signal }) {
            const requestConfig = this.createParamsFromBoardQuery(options, type);

            return new Promise((resolve, reject) => {
                SolutionService.getAll(requestConfig, signal)
                    .then(({ data }) =>
                        resolve({
                            items: this.solutionItems(data.value),
                            count: data['@odata.count'],
                        }),
                    )
                    .catch(reject);
            });
        },
    },
};
